import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type HighlightedEvent, isEventHighlighted} from '@github-ui/timeline-items/HighlightedEvent'
import {wrapElement} from '@github-ui/timeline-items/LayoutHelpers'
import type {SecondaryTimelineSource} from '@github-ui/timeline-items/SecondaryTimelineSource'
import {timelineItemKey} from '@github-ui/timeline-items/TimelineItemKey'
import {VALUES} from '@github-ui/timeline-items/Values'
import type {ReactNode, RefObject} from 'react'

import {FailedLoadTimelineItem} from './FailedLoadTimelineItem'
import type {RenderItem, TimelineItem} from './IssueTimeline'
import styles from './IssueTimeline.module.css'
import {type EventProps, IssueTimelineItem} from './IssueTimelineItem'
import {isLoadAllMode, LoadMore, type LoadMoreCallbackFn} from './LoadMore'
import {EndpointSecondaryTimelineSource, RelaySecondaryTimelineSource} from './secondary-timeline-source'

type VirtualIssueTimelinePageProps = {
  timelineItems: TimelineItem[]
  shouldHighlightElement: boolean
  highlight?: HighlightedEvent
  highlightedItemRef: RefObject<HTMLLIElement | null>
  issueId: string
  repositoryId: string
  repositoryNameWithOwner: string
  issueUrl: string
  showCopilotMenuItem?: boolean
  /** @see SecondaryTimelineSource */
  secondaryTimelineMode?: 'relay' | 'endpoint'
  requestFocusAfterLoad: (baseId: string, isComment: boolean, position: 'top' | 'bottom', isLoadAll: boolean) => void
} & EventProps

function shouldAddDivider(items: TimelineItem[], index: number) {
  if (index === 0) {
    return true
  }
  if (items[index - 1]?.type === 'load') {
    return false
  }
  const item = items[index]
  const previousItem = items[index - 1]
  if (!item || !previousItem) {
    return false
  }

  if (item.type === 'event' && previousItem.type === 'event') {
    const eventType = item.item?.__typename
    const previousEventType = previousItem.item?.__typename
    if (eventType && previousEventType) {
      const isMajorEvent = VALUES.timeline.majorEventTypes.includes(eventType)
      const previousIsMajorEvent = VALUES.timeline.majorEventTypes.includes(previousEventType)

      if (isMajorEvent === previousIsMajorEvent) {
        if (eventType !== 'IssueComment' && previousEventType !== 'IssueComment') {
          return false
        }
      }
    }
  }

  return true
}

/** Only one source mounts: the endpoint path never touches a Relay environment, which the Data
 * Router route does not provide. */
export function VirtualIssueTimelinePage(props: VirtualIssueTimelinePageProps): React.ReactElement {
  const Source =
    props.secondaryTimelineMode === 'endpoint' ? EndpointSecondaryTimelineSource : RelaySecondaryTimelineSource

  return (
    <Source timelineItems={props.timelineItems} issueUrl={props.issueUrl}>
      {source => <VirtualIssueTimelinePageRows {...props} secondaryTimelineSource={source} />}
    </Source>
  )
}

function VirtualIssueTimelinePageRows({
  timelineItems,
  shouldHighlightElement,
  highlight,
  viewer,
  issueId,
  repositoryId,
  repositoryNameWithOwner,
  issueUrl,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
  highlightedItemRef,
  showCopilotMenuItem = false,
  requestFocusAfterLoad,
  secondaryTimelineSource,
}: VirtualIssueTimelinePageProps & {secondaryTimelineSource?: SecondaryTimelineSource}): React.ReactElement {
  // Under primer_react_timeline_list_semantics the parent <Timeline> renders as an
  // <ol>, so every row this page emits must be an <li> that is a DIRECT child of
  // that <ol>. That rules out both the <section> grouping and the wrapElement <div>
  // around the load control on the flag-on path.
  const useListSemantics = isFeatureEnabled('primer_react_timeline_list_semantics')

  const renderItems: RenderItem[] = timelineItems.map(
    (timelineItem: TimelineItem, index, initialTimelineItems): RenderItem => {
      if (timelineItem.type === 'load') {
        const fullType: 'load-top' | 'load-bottom' = `${timelineItem.type}-${timelineItem.position}`

        // Anchor focus on the item just before the control: the newly-loaded rows
        // are inserted after it, so the hook focuses the first of them after the load.
        const baseItem = initialTimelineItems[index - 1]
        const baseId = baseItem?.type === 'event' && baseItem.item ? timelineItemKey(baseItem.item) : undefined
        const isComment = baseItem?.type === 'event' && baseItem.item?.__typename === 'IssueComment'

        // The hook focuses the new content after either kind of load; isLoadAll only
        // tells it *when* it's safe to do so. "Load all" (fewer than a page remaining)
        // streams across commits and unmounts the control, so the hook waits for it to
        // go; a partial "Load more" lands in one commit. Shares the predicate with
        // LoadMore so the two thresholds can't drift.
        const isLoadAll = isLoadAllMode(timelineItem.numberOfRemainingItems)

        // Record the focus intent synchronously when the load is triggered (before
        // the async Relay load), so the post-commit hook can restore focus once the
        // load lands. See useFocusAfterTimelineLoad.
        const withFocusRequest =
          (load: LoadMoreCallbackFn): LoadMoreCallbackFn =>
          (count, options) => {
            requestFocusAfterLoad(baseId ?? '', isComment, timelineItem.position, isLoadAll)
            load(count, options)
          }

        const wrappedElement = (
          <div className={styles.loadMoreWrapper}>
            <LoadMore
              key={fullType}
              type={fullType}
              loadFromTopFn={withFocusRequest(timelineItem.loadFromTop)}
              loadFromBottomFn={withFocusRequest(timelineItem.loadFromBottom)}
              numberOfRemainingItems={timelineItem.numberOfRemainingItems}
              lastItemInTopTimelineIsComment
              firstItemInBottomTimelineIsComment
              isCurrentlyLoadingBackItems={timelineItem.isCurrentlyLoading}
            >
              Load more
            </LoadMore>
          </div>
        )

        return {
          isAddedToGroupedEvents: false,
          timelineItem,
          // Under list semantics the "Load more" control renders as a plain <li> (a direct
          // child of the Timeline <ol>). We keep the wrapElement layout wrapper *inside* the
          // <li> so the control retains its non-leading 56px offset and the
          // data-wrapper-timeline-id focus anchor. A wrapper nested inside an <li> is valid
          // list markup; only wrappers between the <ol> and its <li> children are not.
          render: useListSemantics ? (
            <li key={fullType}>{wrapElement(wrappedElement, undefined, fullType)}</li>
          ) : (
            wrapElement(wrappedElement, undefined, fullType)
          ),
        }
      }

      if (timelineItem.item == null) {
        return {
          isAddedToGroupedEvents: false,
          timelineItem,
          render: <FailedLoadTimelineItem key="failed-load-item" />,
        }
      }

      const addDivider = shouldAddDivider(timelineItems || [], index)
      const isHighlighted = shouldHighlightElement && highlight && isEventHighlighted(timelineItem.item, highlight)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const ownerLogin = repositoryNameWithOwner.split('/')[0]!

      return {
        isAddedToGroupedEvents: false,
        timelineItem,
        render: (
          <IssueTimelineItem
            key={timelineItemKey(timelineItem.item)}
            item={timelineItem}
            issueId={issueId}
            repositoryId={repositoryId}
            repositoryNameWithOwner={repositoryNameWithOwner}
            ownerLogin={ownerLogin}
            issueUrl={issueUrl}
            viewer={viewer}
            onCommentChange={onCommentChange}
            onCommentReply={onCommentReply}
            onCommentEditCancel={onCommentEditCancel}
            refAttribute={isHighlighted ? highlightedItemRef : undefined}
            optionConfig={optionConfig}
            isHighlighted={isHighlighted}
            addDivider={addDivider}
            secondaryTimelineSource={secondaryTimelineSource}
            showCopilotMenuItem={showCopilotMenuItem}
          />
        ),
      }
    },
  )

  // Flag-on: emit each row's <li> flat so it is a direct child of the <ol>.
  // Flag-off (production): group consecutive non-comment events into
  // <section aria-label="Events"> for the screen-reader landmark, unchanged.
  const reactElements: ReactNode[] = useListSemantics
    ? renderItems.map(renderItem => renderItem.render)
    : renderItems.reduce((newArr, currentItem, currentIndex, allItems) => {
        // This is grouping event items that are rendered next to eachother
        // inside <section> tags for adding the correct landmarks for screenreaders
        //
        // All events that are not comments, will be grouped.
        //
        // https://github.com/github/accessibility/issues/5224#issuecomment-1846919306

        if (currentItem.isAddedToGroupedEvents) return newArr
        if (currentItem.timelineItem.type !== 'event' || currentItem.timelineItem.item?.__typename === 'IssueComment') {
          currentItem.isAddedToGroupedEvents = true
          newArr.push(currentItem.render)
          return newArr
        }

        const endSectionIndex = allItems.findIndex((renderItem, index) => {
          return (
            index > currentIndex &&
            (renderItem.timelineItem.type !== 'event' || renderItem.timelineItem.item?.__typename === 'IssueComment')
          )
        })
        const sectionElement = (
          <section
            key={`events-${currentItem.timelineItem.item ? timelineItemKey(currentItem.timelineItem.item) : ''}`}
            aria-label="Events"
          >
            {allItems.slice(currentIndex, endSectionIndex > -1 ? endSectionIndex : undefined).map(item => {
              item.isAddedToGroupedEvents = true
              return item.render
            })}
          </section>
        )

        newArr.push(sectionElement)
        return newArr
      }, [] as ReactNode[])
  return <>{reactElements}</>
}
